import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Activity, Server, Cpu, HardDrive, RefreshCw, AlertTriangle, CheckCircle, ExternalLink, Power, Download } from 'lucide-react';

const Container = styled.div`
  padding: 30px;
  max-width: 1000px;
  margin: 0 auto;
  height: 100%;
  overflow-y: auto;
  background: ${props => props.theme.background};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: ${props => props.theme.text};
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StatusBadge = styled.div`
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  background: ${props => props.online ? '#28a74520' : '#dc354520'};
  color: ${props => props.online ? '#28a745' : '#dc3545'};
  border: 1px solid ${props => props.online ? '#28a74540' : '#dc354540'};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
`;

const Card = styled.div`
  background: ${props => props.theme.surface};
  border-radius: 16px;
  padding: 24px;
  border: 1px solid ${props => props.theme.border};
  box-shadow: ${props => props.theme.shadow};
  transition: transform 0.2s, box-shadow 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(0,0,0,0.1);
  }
`;

const CardTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${props => props.theme.text};
`;

const ServiceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ServiceItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 1px solid ${props => props.theme.border}40;
  
  &:last-child {
    border-bottom: none;
  }
`;

const ServiceInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const ServiceName = styled.span`
  font-weight: 500;
  font-size: 15px;
`;

const ServiceDetails = styled.span`
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
`;

const ServiceStatus = styled.span`
  font-size: 12px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 6px;
  background: ${props => props.active ? '#28a74520' : '#6c757d20'};
  color: ${props => props.active ? '#28a745' : '#6c757d'};
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StatLabel = styled.span`
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
`;

const StatValue = styled.span`
  font-size: 20px;
  font-weight: 700;
`;

const ActionButton = styled.button`
  background: ${props => props.variant === 'danger' ? props.theme.danger : props.theme.primary};
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.9;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AgentDashboard = () => {
  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [online, setOnline] = useState(false);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const response = await fetch('http://127.0.0.1:8001/dashboard-status');
      if (response.ok) {
        const data = await response.json();
        setStatusData(data);
        setOnline(true);
      } else {
        setOnline(false);
      }
    } catch (error) {
      setOnline(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 5000); // Poll every 5s
    return () => clearInterval(interval);
  }, []);

  const handleRestart = async () => {
    if (window.confirm('Are you sure you want to restart the Local Agent?')) {
      try {
        await fetch('http://127.0.0.1:8001/restart', { method: 'POST' }); // I need to add this endpoint
        alert('Restart command sent. Please wait a few seconds for the agent to come back online.');
      } catch (e) {
        // Fetch might fail because agent stops immediately
      }
    }
  };

  return (
    <Container>
      <Header>
        <Title>
          <Server size={32} color="#007bff" />
          VidoAI Local Agent
        </Title>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <StatusBadge online={online}>
            {online ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
            {online ? 'Online' : 'Offline'}
          </StatusBadge>
          <ActionButton onClick={fetchStatus} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </ActionButton>
        </div>
      </Header>

      {!online && (
        <Card style={{ textAlign: 'center', padding: '40px', border: '1px solid #dc3545' }}>
          <AlertTriangle size={48} color="#dc3545" style={{ marginBottom: '16px' }} />
          <h2 style={{ marginBottom: '12px' }}>Local Agent Connection Failed</h2>
          <p style={{ color: '#6c757d', marginBottom: '24px' }}>
            We couldn't connect to the local agent on port 8001. 
            Please make sure the agent is running on your computer.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <ActionButton onClick={fetchStatus}>Try Again</ActionButton>
            <ActionButton 
              as="a" 
              href="/downloads/VidoAI_Local_Agent_v2.zip" 
              download 
              style={{ background: '#28a745', borderColor: '#28a745', color: '#ffffff', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            >
              <Download size={16} />
              Download Local Agent (.zip)
            </ActionButton>
          </div>
        </Card>
      )}

      {online && statusData && (
        <Grid>
          <Card>
            <CardTitle>
              <Activity size={20} color="#28a745" />
              Services Status
            </CardTitle>
            <ServiceList>
              {statusData.services.map((service, idx) => (
                <ServiceItem key={idx}>
                  <ServiceInfo>
                    <ServiceName>{service.name}</ServiceName>
                    <ServiceDetails>{service.details}</ServiceDetails>
                  </ServiceInfo>
                  <ServiceStatus active={service.status === 'Active'}>
                    {service.status}
                  </ServiceStatus>
                </ServiceItem>
              ))}
            </ServiceList>
          </Card>

          <Card>
            <CardTitle>
              <Cpu size={20} color="#007bff" />
              System Performance
            </CardTitle>
            <StatGrid>
              <StatItem>
                <StatLabel>CPU Usage</StatLabel>
                <StatValue>{statusData.system.cpu}</StatValue>
              </StatItem>
              <StatItem>
                <StatLabel>RAM Usage</StatLabel>
                <StatValue>{statusData.system.ram}</StatValue>
              </StatItem>
              <StatItem>
                <StatLabel>Platform</StatLabel>
                <StatValue style={{ fontSize: '16px' }}>{statusData.system.platform}</StatValue>
              </StatItem>
              <StatItem>
                <StatLabel>Agent Status</StatLabel>
                <StatValue style={{ fontSize: '16px', color: '#28a745' }}>{statusData.status}</StatValue>
              </StatItem>
            </StatGrid>
            
            <div style={{ marginTop: '30px', display: 'flex', gap: '12px' }}>
              <ActionButton variant="primary" style={{ flex: 1 }} onClick={handleRestart}>
                <RefreshCw size={16} />
                Restart Agent
              </ActionButton>
            </div>
          </Card>

          <Card style={{ gridColumn: '1 / -1' }}>
            <CardTitle>
              <HardDrive size={20} color="#6f42c1" />
              Agent Configuration
            </CardTitle>
            <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: '20px' }}>
              The local agent provides low-level file system access and heavy video processing (FFmpeg) 
              that browsers cannot perform directly.
            </p>
            <div style={{ display: 'flex', gap: '16px' }}>
              <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px', flex: 1 }}>
                <div style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase' }}>Port</div>
                <div style={{ fontWeight: '600' }}>8001</div>
              </div>
              <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px', flex: 1 }}>
                <div style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase' }}>Protocol</div>
                <div style={{ fontWeight: '600' }}>HTTP (REST API)</div>
              </div>
              <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px', flex: 1 }}>
                <div style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase' }}>Features</div>
                <div style={{ fontWeight: '600' }}>FFmpeg, Whisper, Pickers</div>
              </div>
            </div>
          </Card>
        </Grid>
      )}
    </Container>
  );
};

export default AgentDashboard;
